import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, Sparkles, Star, ChevronRight, Trophy } from "lucide-react";
import api from "../lib/api";
import BusinessCard from "../components/BusinessCard";
import CompactBusinessCard from "../components/CompactBusinessCard";
import SearchAutocomplete from "../components/SearchAutocomplete";
import GeoLocateButton from "../components/GeoLocateButton";
import { CATEGORY_META } from "../lib/constants";
import { trackEvent } from "../lib/analytics";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/14d892a8-deb9-48c2-a365-b961a88d2ed7/images/ac31dddd7f5185a60718ac2da80f2fee71a0f237d9225c2d68fc6d519ab2bc9a.png";

export default function HomePage() {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("conakry");
  const [cities, setCities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [popular, setPopular] = useState([]);
  const [topWeek, setTopWeek] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [stats, setStats] = useState({ businesses: 0, reviews: 0, categories: 12 });
  const navigate = useNavigate();
  const searchWrapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, f, p, all, t] = await Promise.all([
          api.get("/cities"),
          api.get("/businesses/featured", { params: { limit: 6 } }),
          api.get("/businesses/popular", { params: { limit: 4 } }),
          api.get("/businesses", { params: { limit: 1 } }),
          api.get("/businesses/top-this-week", { params: { city: "conakry", limit: 10 } }),
        ]);
        setCities(c.data);
        setFeatured(f.data);
        setPopular(p.data);
        setTopWeek(t.data);
        const totalBiz = all.data.total || 0;
        // Derive review count rough sum from featured + popular sample
        let totalReviews = 0;
        [...f.data, ...p.data].forEach((b) => { totalReviews += b.review_count || 0; });
        setStats({
          businesses: Math.max(1200, totalBiz),
          reviews: Math.max(8400, totalReviews),
          categories: 12,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("home data fetch failed:", e?.message);
        }
      }
    })();
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (city) params.set("city", city);
    if (q) trackEvent("search", { search_term: q, city, source: "home_hero" });
    navigate(`/search?${params.toString()}`);
  };

  const quickPills = [
    { label: "Restaurants", slug: "restaurants" },
    { label: "Hôtels", slug: "hotels" },
    { label: "Pharmacies", slug: "sante" },
    { label: "Banques", slug: "banques" },
    { label: "Taxis", slug: "transport" },
  ];

  return (
    <div className="bg-lokaso-sand" data-testid="home-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(15,50,32,0.85), rgba(15,50,32,0.95)), url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* flag bar */}
        <div className="absolute top-0 left-0 right-0 flex h-1.5">
          <div className="flex-1" style={{ background: "#C4291C" }} />
          <div className="flex-1" style={{ background: "#E8A020" }} />
          <div className="flex-1" style={{ background: "#1A5C35" }} />
        </div>

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-24 pb-32 text-center text-lokaso-sand">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-[0.18em] font-semibold mb-6">
            <span className="lokaso-flag-stripe h-3"><span /><span /><span /></span>
            Guinée · Conakry · Kindia · Labé
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Trouvez les meilleurs services<br className="hidden md:block" /> en Guinée
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85 max-w-2xl mx-auto">
            Des milliers d'entreprises locales à portée de main. Restaurants, hôtels, pharmacies, banques et plus.
          </p>

          <form onSubmit={submitSearch} className="hero-search mt-10 mx-auto max-w-3xl p-2 flex flex-col md:flex-row gap-2 items-stretch relative" data-testid="hero-search-form" ref={searchWrapRef}>
            <div className="flex-1 flex items-center gap-2 px-4 relative">
              <Search className="w-5 h-5 text-stone-500" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowAutocomplete(true); }}
                onFocus={() => setShowAutocomplete(true)}
                placeholder="Restaurant, pharmacie, hôtel..."
                className="w-full h-12 bg-transparent text-lokaso-dark placeholder:text-stone-400 focus:outline-none"
                data-testid="hero-search-input"
                autoComplete="off"
              />
            </div>
            <div className="hidden md:block w-px bg-stone-200 my-2" />
            <div className="flex items-center gap-2 px-4">
              <MapPin className="w-5 h-5 text-stone-500" />
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-12 bg-transparent text-lokaso-dark focus:outline-none pr-2"
                data-testid="hero-city-select"
              >
                {cities.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-gold h-12 px-6 md:px-8 rounded-xl whitespace-nowrap" data-testid="hero-search-submit">
              Chercher
            </button>

            {showAutocomplete && (
              <SearchAutocomplete
                query={q}
                citySlug={city}
                onClose={() => setShowAutocomplete(false)}
                onSelect={() => setShowAutocomplete(false)}
              />
            )}
          </form>

          <div className="mt-4 flex justify-center">
            <GeoLocateButton
              variant="hero"
              onLocated={({ city: c }) => {
                if (c) setCity(c);
                const params = new URLSearchParams();
                if (c) params.set("city", c);
                navigate(`/search?${params.toString()}`);
              }}
            />
          </div>

          <div className="mt-6 flex flex-wrap gap-2 justify-center" data-testid="hero-quick-pills">
            {quickPills.map((p) => (
              <Link key={p.slug} to={`/search?category=${p.slug}`} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-sm border border-white/20">
                {p.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="-mt-12 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-3 md:gap-6 bg-white rounded-3xl shadow-xl border border-stone-200 px-6 py-6 md:py-8" data-testid="stats-bar">
            <div className="text-center">
              <div className="stat-num">{stats.businesses.toLocaleString("fr-FR")}+</div>
              <p className="text-xs md:text-sm text-stone-600 mt-1">Entreprises listées</p>
            </div>
            <div className="text-center border-x border-stone-200">
              <div className="stat-num">{stats.reviews.toLocaleString("fr-FR")}+</div>
              <p className="text-xs md:text-sm text-stone-600 mt-1">Avis clients vérifiés</p>
            </div>
            <div className="text-center">
              <div className="stat-num">{stats.categories}</div>
              <p className="text-xs md:text-sm text-stone-600 mt-1">Catégories couvertes</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-lokaso-dark">Explorer par catégorie</h2>
            <p className="text-stone-600 mt-1">Toutes les entreprises de Guinée, par secteur</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4" data-testid="categories-grid">
          {Object.entries(CATEGORY_META).map(([slug, meta]) => (
            <Link
              to={`/search?category=${slug}`}
              key={slug}
              className="category-tile rounded-2xl border border-stone-200 bg-white p-5 flex items-center gap-3"
              style={{ "--tile-color": meta.color }}
              data-testid={`category-tile-${slug}`}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0"
                style={{ background: `${meta.color}1f`, color: meta.color }}
              >
                {meta.emoji}
              </div>
              <div className="font-display text-base md:text-lg font-semibold text-lokaso-dark leading-snug">
                {meta.name}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TOP 10 THIS WEEK — hidden until at least one biz has reviews */}
      {topWeek.length > 0 && topWeek.some((b) => (b.review_count || 0) > 0) && (
        <section className="max-w-7xl mx-auto px-4 md:px-10 pb-4" data-testid="top-week-section">
          <div className="flex items-end justify-between mb-5 px-2 md:px-0">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Trophy className="text-lokaso-gold" /> Top 10 cette semaine à Conakry
              </h2>
              <p className="text-sm text-stone-600 mt-1">Les mieux notés avec une vraie communauté d'avis</p>
            </div>
          </div>
          <div className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible no-scrollbar pb-3 -mx-4 px-4 md:mx-0 md:px-0" data-testid="top-week-list">
            {topWeek.map((b, i) => (
              <CompactBusinessCard key={b.id || b.slug} business={b} rank={i + 1} />
            ))}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Star className="text-lokaso-gold fill-lokaso-gold" /> Les mieux notés
          </h2>
          <Link to="/search" className="text-sm font-medium text-lokaso-green hover:underline inline-flex items-center" data-testid="featured-see-all">
            Voir tout <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-grid">
          {featured.map((b) => <BusinessCard key={b.id} business={b} />)}
        </div>
      </section>

      {/* POPULAR */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-20">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Sparkles className="text-lokaso-red" /> Les plus populaires
          </h2>
          <Link to="/search?sort=reviews" className="text-sm font-medium text-lokaso-green hover:underline inline-flex items-center" data-testid="popular-see-all">
            Voir tout <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="popular-grid">
          {popular.map((b) => <BusinessCard key={b.id} business={b} />)}
        </div>
      </section>

      {/* OWNER CTA */}
      <section className="mt-24 bg-lokaso-dark text-lokaso-sand">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-lokaso-gold uppercase tracking-[0.2em] text-xs font-semibold">Pour les entreprises</p>
            <h3 className="font-display text-3xl md:text-5xl font-bold mt-2 leading-tight">
              Vous avez une entreprise en Guinée ?
            </h3>
            <p className="mt-4 text-white/80 max-w-md">
              Inscrivez votre entreprise sur Lokaso et atteignez des milliers de clients potentiels. Création gratuite, en moins de 5 minutes.
            </p>
            <Link to="/business/register" className="btn-gold mt-6 inline-flex items-center gap-2" data-testid="owner-cta-button">
              Inscrire mon entreprise gratuitement
            </Link>
          </div>
          <div className="hidden md:flex justify-end">
            <div className="relative rounded-3xl bg-gradient-to-br from-lokaso-green via-emerald-700 to-emerald-900 p-8 max-w-sm">
              <div className="lokaso-flag-stripe h-8 mb-4"><span /><span /><span /></div>
              <h4 className="font-display text-2xl font-bold">Plans flexibles</h4>
              <ul className="mt-4 space-y-2 text-sm text-white/90">
                <li>✓ Gratuit pour commencer</li>
                <li>✓ Featured · 100 000 GNF/mois</li>
                <li>✓ Premium · 300 000 GNF/mois</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
